import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { useReveal } from "@/hooks/useReveal";
import { easing } from "@/lib/animations";
import { useCart } from "@/context/CartContext";
import {
    PRODUCTS,
    CATEGORIES,
    SORT_OPTIONS,
    formatPrice,
} from "@/data/products";

const SIZE_FALLBACK = ["XS", "S", "M", "L", "XL"];

const ShopProductCard = ({ product, index }) => {
    const [hover, setHover] = useState(false);
    const [size, setSize] = useState(product.sizes?.[Math.floor(product.sizes.length / 2)] || "M");
    const [added, setAdded] = useState(false);
    const { addItem } = useCart();
    const [ref, inView] = useReveal({ threshold: 0.08 });

    const sizes = product.sizes && product.sizes.length ? product.sizes : SIZE_FALLBACK;

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem({
            id: product.n,
            slug: product.slug,
            name: product.name,
            priceEUR: product.priceEUR,
            size,
            img: product.cover,
        });
        setAdded(true);
        setTimeout(() => setAdded(false), 1600);
    };

    return (
        <motion.div
            ref={ref}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
            transition={{
                duration: 0.9,
                delay: 0.08 + (index % 3) * 0.06,
                ease: easing.primary,
            }}
            data-testid={`shop-product-${product.slug}`}
            style={{ position: "relative" }}
        >
            <div
                style={{
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    background: product.bgTone,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Link
                    to={`/product/${product.slug}`}
                    aria-label={product.name}
                    data-testid={`shop-${product.slug}-link`}
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        textDecoration: "none",
                    }}
                />
                {product.badge && (
                    <span
                        style={{
                            position: "absolute",
                            top: 14,
                            left: 14,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 9,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "#0c0c0c",
                            padding: "4px 8px",
                            zIndex: 2,
                        }}
                    >
                        [ {product.badge} ]
                    </span>
                )}
                <span
                    style={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 9,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(12,12,12,0.55)",
                        zIndex: 2,
                    }}
                >
                    {product.n}
                </span>
                <motion.img
                    layoutId={`product-cover-${product.slug}`}
                    src={product.cover}
                    alt={product.name}
                    draggable={false}
                    style={{
                        width: "82%",
                        height: "82%",
                        objectFit: "contain",
                        transform: hover ? "scale(1.06)" : "scale(1)",
                        transition: "transform 1.4s cubic-bezier(0.22, 1, 0.36, 1)",
                        mixBlendMode: "multiply",
                        pointerEvents: "none",
                    }}
                    data-shared-id={`product-cover-${product.slug}`}
                />

                <motion.div
                    initial={false}
                    animate={{ y: hover ? "0%" : "100%" }}
                    transition={{ duration: 0.5, ease: easing.primary }}
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 3,
                        background: "rgba(12, 12, 12, 0.93)",
                        backdropFilter: "blur(6px)",
                        padding: "16px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            gap: 6,
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                        }}
                    >
                        {sizes.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setSize(s)}
                                data-testid={`shop-${product.slug}-size-${s}`}
                                style={{
                                    flex: 1,
                                    minWidth: 32,
                                    background:
                                        size === s ? "#fff" : "transparent",
                                    color: size === s ? "#0c0c0c" : "#fff",
                                    border:
                                        "1px solid rgba(255,255,255,0.55)",
                                    fontFamily:
                                        "'Space Grotesk', Manrope, sans-serif",
                                    fontWeight: 600,
                                    fontSize: 11,
                                    letterSpacing: "0.12em",
                                    padding: "6px 0",
                                    cursor: "pointer",
                                    transition:
                                        "background 0.25s, color 0.25s",
                                }}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={handleAdd}
                        data-testid={`shop-${product.slug}-add`}
                        style={{
                            width: "100%",
                            background: "#fff",
                            color: "#0c0c0c",
                            border: "none",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            padding: "12px 0",
                            cursor: "pointer",
                        }}
                    >
                        {added ? `Added · ${size}` : "Add to Cart"}
                    </button>
                </motion.div>
            </div>
            <Link
                to={`/product/${product.slug}`}
                style={{
                    paddingTop: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    color: "rgba(255,255,255,0.92)",
                    textDecoration: "none",
                }}
            >
                <span
                    style={{
                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                    }}
                >
                    {product.name}
                </span>
                <span
                    style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 12,
                        letterSpacing: "0.12em",
                        color: "rgba(255,255,255,0.65)",
                    }}
                >
                    {formatPrice(product.priceEUR)}
                </span>
            </Link>
        </motion.div>
    );
};

/* Filter pill — quiet brutalist tag */
const FilterPill = ({ active, label, onClick, testId }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        style={{
            background: active ? "#fff" : "transparent",
            color: active ? "#0c0c0c" : "rgba(255,255,255,0.85)",
            border: "1px solid rgba(255,255,255,0.35)",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            padding: "10px 18px",
            cursor: "pointer",
            transition: "background 0.3s, color 0.3s, border-color 0.3s",
        }}
    >
        {label}
    </button>
);

export default function Shop() {
    const [category, setCategory] = useState("all");
    const [sort, setSort] = useState("default");
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        let list = PRODUCTS.slice();
        if (category !== "all") list = list.filter((p) => p.category === category);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    p.n.includes(q) ||
                    p.materials.toLowerCase().includes(q),
            );
        }
        if (sort === "price-asc") list.sort((a, b) => a.priceEUR - b.priceEUR);
        if (sort === "price-desc") list.sort((a, b) => b.priceEUR - a.priceEUR);
        if (sort === "name-asc") list.sort((a, b) => a.name.localeCompare(b.name));
        return list;
    }, [category, sort, search]);

    return (
        <PageShell
            sectionNumber="03"
            eyebrow="The Field · Open"
            title="Lookbook"
            crumb="Lookbook"
            data-testid="page-shop"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(36px, 4vw, 64px)",
                }}
            >
                {/* INTRO — short manifesto for the catalog */}
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4, ease: easing.primary }}
                    style={{
                        margin: 0,
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "clamp(16px, 1.3vw, 21px)",
                        lineHeight: 1.55,
                        color: "rgba(255,255,255,0.78)",
                        maxWidth: "60ch",
                    }}
                    data-testid="shop-intro"
                >
                    Twelve artifacts. Heavy cotton, raw denim, boiled wool.
                    Each one is cut, sewn and finished slowly in Poland —
                    built to outlast the season that demanded it.
                </motion.p>

                {/* TOOLBAR — categories + sort + search */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 18,
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderTop: "1px solid rgba(255,255,255,0.14)",
                        borderBottom: "1px solid rgba(255,255,255,0.14)",
                        padding: "20px 0",
                    }}
                    data-testid="shop-toolbar"
                >
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        {CATEGORIES.map((c) => (
                            <FilterPill
                                key={c.id}
                                active={category === c.id}
                                label={c.label}
                                onClick={() => setCategory(c.id)}
                                testId={`shop-filter-${c.id}`}
                            />
                        ))}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            flexWrap: "wrap",
                        }}
                    >
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search artifacts…"
                            data-testid="shop-search"
                            style={{
                                background: "transparent",
                                border: "1px solid rgba(255,255,255,0.25)",
                                borderRadius: 0,
                                color: "#fff",
                                fontFamily:
                                    "'Space Grotesk', Manrope, sans-serif",
                                fontSize: 13,
                                padding: "10px 14px",
                                letterSpacing: "0.02em",
                                outline: "none",
                                minWidth: 180,
                            }}
                        />
                        <label
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.24em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.6)",
                            }}
                        >
                            Sort
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                data-testid="shop-sort"
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(255,255,255,0.25)",
                                    color: "#fff",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                }}
                            >
                                {SORT_OPTIONS.map((s) => (
                                    <option key={s.id} value={s.id} style={{ background: "#0c0c0c" }}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                {/* RESULT META */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.55)",
                    }}
                    data-testid="shop-meta"
                >
                    <span>
                        {String(filtered.length).padStart(2, "0")} ·{" "}
                        Artifacts
                    </span>
                    <span>Field 001 — 002</span>
                </div>

                {/* GRID */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: "clamp(20px, 2vw, 36px)",
                    }}
                    data-testid="shop-grid"
                >
                    {filtered.map((p, i) => (
                        <ShopProductCard key={p.n} product={p} index={i} />
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div
                        style={{
                            padding: "60px 0",
                            textAlign: "center",
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 18,
                            color: "rgba(255,255,255,0.55)",
                        }}
                        data-testid="shop-empty"
                    >
                        Nothing in this field. Try a different cut.
                    </div>
                )}
            </div>
        </PageShell>
    );
}
